import { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { IconCogOutline, IconFileOutline, IconMailOutline } from '@zillow/constellation';
import {
  Anchor,
  Box,
  Heading,
  Image,
  List,
  Paragraph,
  Popover,
  Tag,
  Text,
  TextButton,
  Toast,
  TriggerText,
  useToast,
} from '@zillow/constellation-10';

import PropertiesActions from '../../../../actions/PropertiesActions';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import RentGuaranteeShieldStar from '../../../../images/RentGuaranteeShieldStar.svg';
import EditPropertyTypeMenu from '../../../EditPropertyTypeMenu';

import { RMWContext } from '../../../../providers/RMWContext';
import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import numberUtils from '../../../../utils/numberUtils';
import propertyUtils from '../../../../utils/propertyUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';
import accessUtils from '../../../../utils/accessUtils';

import ReviewPageCSEvents from '../../../../model/ClickstreamEvent/ReviewPageCSEvents';
import SingleFamilyCSEvents from '../../../../model/ClickstreamEvent/TotalMonthlyPriceWithFee/SingleFamilyCSEvents';

import AddPropertyConstants from '../../../../constants/AddPropertyConstants';

import RentalProtectionSelectionsModal from '../../../Modal/RentalProtectionSelectionsModal';
import RentGuaranteePolicyDropModal from '../../../Modal/RentGuaranteePolicyDropModal';
import SecurityDepositUpdateFailureModal from '../../../Modal/SecurityDepositUpdateFailureModal';
import SecurityDepositUpdateOnRentGuaranteeDropModal from '../../../Modal/SecurityDepositUpdateOnRentGuaranteeDropModal';

import AddPropertyModal from '../../../Modal/AddPropertyModal';
import { PropertyPhoto } from './PropertyPhoto';

import { addressDetailsStyles, StyledVerticalDivider } from '../styles';
import { singleTextLineStyles } from '../../../../styles';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { PremiumCoverage } from '../../../../model/RentGuarantee';
import type { LegalDisclaimerProps } from '../../../../model/RentGuarantee/LegalDisclaimer';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';

const nullPlaceholder = '--';

export const baseRentPopoverContents = ({
  currentProperty,
  user,
  triggerObject,
  triggerLocation,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerObject: string;
  triggerLocation: string;
}) => {
  return {
    header: <Heading level={6}>Base rent</Heading>,
    body: (
      <Paragraph>
        This amount reflects your base monthly rent only. To comply with{' '}
        <Anchor
          target="_blank"
          href="https://www.zillow.com/rentals-network/fee-transparency-regulations"
          onClick={() => {
            SingleFamilyCSEvents.RegulationsCTA({
              currentProperty,
              user,
              triggerObject,
              triggerLocation,
            });
          }}
          data-eventid={SingleFamilyCSEvents.RegulationsCTA.data_event_id}
        >
          fee transparency regulations
        </Anchor>{' '}
        in your area, any required monthly fees you&apos;ve entered will be automatically combined with base rent after
        publishing to calculate and display a “total price” on your listing. This helps renters understand the full
        monthly cost of living in the home.
      </Paragraph>
    ),
  };
};

const styles = {
  streetText: {
    mobile: 'body-bold',
    desktop: 'heading-md',
  },
  streetTextMF: {
    mobile: 'heading-sm',
    desktop: 'heading-sm',
  },
  stateText: {
    mobile: 'body-sm',
    desktop: 'body',
  },
  stateTextMF: {
    mobile: 'body-sm-bold',
    desktop: 'body-sm-bold',
  },
  details: {
    mobile: 'body-xs',
    desktop: 'body-bold',
  },
  detailsText: {
    mobile: 'body-xs',
    desktop: 'body',
  },
  button: {
    mobile: 'sm',
    desktop: 'md',
  },
} as const;

const getBedroomDisplay = (value: number | string) => {
  if (value === 0 || value === '0') {
    return 'Studio';
  }

  return value;
};

const PropertyCard = () => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();

  const {
    paths: { base },
  } = useContext(RMWContext);

  const {
    modularListingState: { isInReactivationFlow },
  } = useContext(ModularListingContext);

  const {
    active,
    appConfigCanAccessTotalMonthlyPriceWithFee,
    applicationsSummaryListingAlias,
    canAccessApplicationsOnMFFeedConnect,
    canAccessUserManagementApplicationPermission,
    city,
    cookieEnabledServices,
    currentProperty,
    dateLastActivatedTimestamp,
    displayStatus,
    floorplans,
    units,
    isMultifamily,
    isPostingPathAdmin,
    isRentalProtectionEnabled,
    isSecurityDepositWaived,
    isSelfServeOnboardingInProgress,
    isTotalMonthlyPriceWithFeeEligible,
    listingAlias,
    premiumCoverages,
    price,
    securityDeposit,
    state,
    status,
    street,
    totalNumberOfActiveApplications,
    totalNumberOfDeclinedApplications,
    totalNumberOfWithdrawnApplications,
    unit,
    user,
    zip,
  } = useSelector((state) => {
    return {
      active: state.properties.currentProperty.active,
      appConfigCanAccessTotalMonthlyPriceWithFee: state.app.appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
      applicationsSummary: state.properties.currentProperty.applicationsSummary,
      applicationsSummaryListingAlias: state.properties.currentProperty.applicationsSummary.listingAlias,
      canAccessApplicationsOnMFFeedConnect: state.app.appConfig.config.features.canAccessApplicationsOnMFFeedConnect,
      canAccessUserManagementApplicationPermission: state.user.features.canAccessUserManagementApplicationPermission,
      city: state.properties.currentProperty.city,
      cookieEnabledServices: state.app.cookieEnabledServices,
      currentProperty: state.properties.currentProperty,
      dateLastActivatedTimestamp: state.properties.currentProperty.dateLastActivatedTimestamp,
      displayStatus: state.properties.currentProperty.syndication.overview.displayStatus,
      floorplans: state.properties.currentProperty.floorplans,
      units: state.properties.currentProperty.units,
      isMultifamily: state.properties.currentProperty.isMultifamily,
      isPostingPathAdmin: state.user.isPostingPathAdmin,
      isRentalProtectionEnabled: state.properties.currentProperty.rentalProtection.isEnabled,
      isSecurityDepositWaived: state.properties.currentProperty.rentalProtection.isSecurityDepositWaived,
      isSelfServeOnboardingInProgress:
        state.properties.currentProperty.selfServeOnboardingStatus.isSelfServeOnboardingInProgress,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      listingAlias: state.properties.currentProperty.listingAlias,
      premiumCoverages: state.properties.currentProperty.rentalProtection.premiumCoverages,
      price: state.properties.currentProperty.price,
      securityDeposit: state.properties.currentProperty.securityDeposit,
      state: state.properties.currentProperty.state,
      status: state.properties.currentProperty.selfServeOnboardingStatus.status,
      street: state.properties.currentProperty.street,
      totalNumberOfActiveApplications:
        state.properties.currentProperty.applicationsSummary.rentalApplicationData.totalNumberOfActiveApplications,
      totalNumberOfDeclinedApplications:
        state.properties.currentProperty.applicationsSummary.rentalApplicationData.totalNumberOfDeclinedApplications,
      totalNumberOfWithdrawnApplications:
        state.properties.currentProperty.applicationsSummary.rentalApplicationData.totalNumberOfWithdrawnApplications,
      unit: state.properties.currentProperty.unit,
      user: state.user,
      zip: state.properties.currentProperty.zip,
    };
  }, shallowEqual);
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));

  const [displayAddPropertyModal, setDisplayAddPropertyModal] = useState(false);
  const [displayRentalProtectionSelectionsModal, setDisplayRentalProtectionSelectionsModal] = useState(false);
  const [showPolicyDropModal, setShowPolicyDropModal] = useState(false);
  const [showSecurityDepositUpdateModal, setShowSecurityDepositUpdateModal] = useState(false);
  const [showSecurityDepositUpdateFailureModal, setShowSecurityDepositUpdateFailureModal] = useState(false);
  const [legalDisclaimer, setLegalDisclaimer] = useState<Array<LegalDisclaimerProps>>([]);

  const canAccessListingQuality = accessUtils.canAccessListingQuality({
    user,
    currentProperty,
  });
  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfigCanAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const selectedCoverageOption = premiumCoverages.find((option: PremiumCoverage) => option.isSelected);
  const rentalProtectionPremium = selectedCoverageOption?.premium / 100;
  const coverage = selectedCoverageOption?.coverage / 100;

  const breakpoint = isScreenMobile ? 'mobile' : 'desktop';
  const totalNumberOfAllApplications =
    totalNumberOfDeclinedApplications + totalNumberOfWithdrawnApplications + totalNumberOfActiveApplications;

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });
  const { formattedStreet, formattedCityStateZip, formattedCompleteAddress } = propertyUtils.formatAddress({
    street,
    city,
    state,
    zip,
    unit,
  });

  const isStudio = floorplans?.[0]?.beds === 0 || floorplans?.[0]?.beds === '0';

  useEffect(() => {
    if (isRentalProtectionEnabled) {
      // @ts-expect-error
      dispatch(PropertiesActions.getDisclaimers(listingAlias, 'generalV2')).then(
        (response: Array<LegalDisclaimerProps>) => {
          setLegalDisclaimer(response);
        },
      );
    }
  }, []);

  const handleEditButtonClick = (isAdminTools = false) => {
    if (isMultifamily || isAdminTools) {
      setDisplayAddPropertyModal(true);

      return;
    }
  };

  const handleDropRentalProtectionClick = () => {
    setDisplayRentalProtectionSelectionsModal(false);
    setShowPolicyDropModal(true);
  };

  const handleRentGuaranteePolicyDrop = () => {
    const dataToApi = { listingAlias };

    dispatch(PropertiesActions.dropRentalProtectionPolicy(dataToApi))
      // @ts-expect-error
      .then(() => {
        setShowPolicyDropModal(false);
        // get this somewhere else than from getRentalProtectionPageForListing response
        //setShowSecurityDepositUpdateModal(true);
      })
      .catch((error: ErrorRMWProps) => {
        console.error('ERROR@dropRentalProtectionPolicy: ', error);
        enqueueToast(<Toast tone="critical">Error dropping Rental Protection, please try again.</Toast>, {
          duration: 10000,
        });
      });
  };

  const handleSecurityDepositUpdate = (updatedDeposit: number) => {
    dispatch(
      PropertiesActions.updateSecurityDepositForListing(listingAlias, numberUtils.asCurrency(updatedDeposit, '0.00')),
    )
      // @ts-expect-error
      .then(() => {
        enqueueToast(<Toast tone="success">You updated your security deposit</Toast>);
      })
      .catch(() => {
        setShowSecurityDepositUpdateFailureModal(true);
        setShowSecurityDepositUpdateModal(false);
      });
  };

  const handleApplicationsClick = () => {
    ReviewPageCSEvents.ApplicationsClick({
      user,
      currentProperty,
    });

    window.router.updateLocation(`${base}/applications?propertyId=${listingAlias}`);
  };

  const renderAddressDetails = () => {
    let priceSource = [];
    if (Array.isArray(floorplans) && floorplans.length > 0) {
      priceSource = floorplans;
    } else if (Array.isArray(units) && units.length > 0) {
      priceSource = units;
    }

    const lowestPrice =
      priceSource.length > 0
        ? priceSource.reduce((min, item) => {
            // Use lowPrice for floorplans, price for units
            const planPrice = Number(item.lowPrice !== undefined ? item.lowPrice : item.price);
            if (planPrice && planPrice > 0) {
              return min === null || planPrice < min ? planPrice : min;
            }
            return min;
          }, null)
        : null;

    return (
      <Box css={addressDetailsStyles}>
        {canAccessListingQuality && (
          <Box>
            <Heading level={3} css={{ textWrap: 'balance' }}>
              {currentProperty.name || formattedStreet}
            </Heading>
            <Paragraph css={{ ...singleTextLineStyles }}>{formattedCompleteAddress}</Paragraph>
            {Boolean(lowestPrice) && (
              <Paragraph textStyle="body-bold" css={{ marginTop: '300' }}>
                Rent starting at {numberUtils.asCurrency(lowestPrice)}
              </Paragraph>
            )}
          </Box>
        )}
        {!canAccessListingQuality &&
          (isMultifamily && !active ? (
            <Box css={{ display: 'flex', alignItems: 'flex-start' }}>
              <div data-testid="AddressDetailsMFContainer">
                <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Text
                    textStyle={styles.streetTextMF[breakpoint]}
                    css={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 'normal',
                    }}
                    asChild
                  >
                    <h4>{formattedStreet}</h4>
                  </Text>
                </Box>
                <Text
                  textStyle={styles.stateTextMF[breakpoint]}
                  css={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '100',
                    md: { marginBottom: '300' },
                  }}
                  asChild
                >
                  <p>{formattedCityStateZip}</p>
                </Text>
                {isSelfServeOnboardingInProgress ? (
                  <Tag tone={getTagTypeByKey(STATUS_KEY.SELF_SERVE_ONBOARDING, status.persistentString)}>
                    {status.display}
                  </Tag>
                ) : (
                  <Tag tone={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, displayStatus)}>{displayStatus}</Tag>
                )}
              </div>
              <Box css={{ marginLeft: '400' }}>
                <EditPropertyTypeMenu />
              </Box>
            </Box>
          ) : (
            <div data-testid="AddressDetailsSFContainer">
              <Text
                textStyle={styles.streetText[breakpoint]}
                css={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 'normal',
                }}
                asChild
              >
                {breakpoint === 'mobile' ? <p>{formattedStreet}</p> : <h3>{formattedStreet}</h3>}
              </Text>
              <Text
                textStyle={styles.stateText[breakpoint]}
                css={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '100',
                  md: { marginBottom: '300' },
                }}
                asChild
              >
                <p>{formattedCityStateZip}</p>
              </Text>
              <Box css={{ display: 'flex' }}>
                <List.Root appearance="none">
                  <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Price">
                    <Text textStyle={styles.details[breakpoint]}>
                      {`${floorplans?.[0]?.price ? `$${numberUtils.prettyPrintNumber(floorplans?.[0]?.price)}` : propertyUtils.getFloorPlanDescription({ units }).displayPrice || nullPlaceholder}`}
                    </Text>
                    <Text textStyle={styles.detailsText[breakpoint]}>
                      /mo{canAccessTotalMonthlyPriceWithFee && !isMultifamily ? ' ' : ''}
                    </Text>
                    {Boolean(canAccessTotalMonthlyPriceWithFee && !isMultifamily) && (
                      <Popover
                        dividers={true}
                        shouldAwaitInteractionResponse={true}
                        tone="neutral"
                        {...baseRentPopoverContents({
                          currentProperty,
                          user,
                          triggerLocation: 'posting_path_review',
                          triggerObject: 'zrm_review_top_of_page',
                        })}
                        trigger={<TriggerText>(base rent)</TriggerText>}
                      />
                    )}
                  </List.Item>
                  {/* ZRM-10673 - aria-hidden on divider <li>'s: contains no content for a screen reader */}
                  <StyledVerticalDivider aria-hidden="true" />
                  <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Bedrooms">
                    <Text textStyle={styles.details[breakpoint]}>
                      {`${getBedroomDisplay(floorplans?.[0]?.beds) || nullPlaceholder}`}
                    </Text>
                    {isStudio === false && <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;bd</Text>}
                  </List.Item>
                  <StyledVerticalDivider aria-hidden="true" />
                  <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Bathrooms">
                    <Text textStyle={styles.details[breakpoint]}>{`${floorplans?.[0]?.baths || nullPlaceholder}`}</Text>
                    <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;ba</Text>
                  </List.Item>
                  <StyledVerticalDivider aria-hidden="true" />
                  <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Sqft">
                    <Text
                      textStyle={styles.details[breakpoint]}
                    >{`${floorplans?.[0]?.squareFeet || nullPlaceholder}`}</Text>
                    <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;sq. ft.</Text>
                  </List.Item>
                </List.Root>
              </Box>
              {isRoomForRent && !isScreenMobile && (
                <Box css={{ display: 'flex', marginTop: '200' }}>
                  <Text textStyle={styles.details[breakpoint]}>Room for rent</Text>
                  {floorplans?.[0]?.roomForRent?.roomSquareFeet && (
                    <div data-testid="WizardModularListing-Review-RoomSqft">
                      <StyledVerticalDivider aria-hidden="true" />
                      <Text
                        textStyle={styles.details[breakpoint]}
                      >{`${floorplans?.[0]?.roomForRent.roomSquareFeet}`}</Text>
                      <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;sq. ft.</Text>
                    </div>
                  )}
                </Box>
              )}
            </div>
          ))}
      </Box>
    );
  };

  const renderAddressHeaderSection = () =>
    isInReactivationFlow && isRentalProtectionEnabled && !active ? (
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        {renderAddressDetails()}
        <Paragraph css={{ marginTop: '200' }}>
          <Image src={RentGuaranteeShieldStar} alt="" css={{ width: '21px', height: '21px', marginRight: '200' }} />
          Rental Protection request started
          <TextButton onClick={() => setDisplayRentalProtectionSelectionsModal(true)} css={{ marginLeft: '200' }}>
            See selections
          </TextButton>
        </Paragraph>
      </Box>
    ) : (
      renderAddressDetails()
    );

  return (
    <Box
      css={{
        display: 'flex',
        mdDown: { flexDirection: 'column', alignItems: 'start' },
        minHeight: { base: '74px', md: '156px' },
      }}
    >
      {!isScreenMobile && <PropertyPhoto />}
      {renderAddressHeaderSection()}

      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginLeft: { lg: '200' },
          marginTop: { mdDown: '200' },
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', lg: 'row' },
            gap: { lg: '400' },
            justifyContent: 'flex-end',
          }}
        >
          {canAccessApplicationsOnMFFeedConnect &&
            canAccessUserManagementApplicationPermission &&
            applicationsSummaryListingAlias !== '' && (
              <TextButton
                tone="neutral"
                icon={<IconFileOutline />}
                size={styles.button[breakpoint]}
                onClick={handleApplicationsClick}
                data-eventid={ReviewPageCSEvents.ApplicationsClick.data_event_id}
              >
                {`Applications (${totalNumberOfAllApplications})`}
              </TextButton>
            )}

          {isMultifamily && dateLastActivatedTimestamp && (
            <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
              <TextButton
                tone="neutral"
                icon={<IconMailOutline />}
                size={styles.button[breakpoint]}
                onClick={() => window.router.updateLocation(`${base}/inbox/${listingAlias}?byProperty=true`)}
              >
                Messages
              </TextButton>
            </Box>
          )}
          {isPostingPathAdmin && (
            <TextButton
              tone="neutral"
              icon={<IconCogOutline />}
              size={styles.button[breakpoint]}
              onClick={() => handleEditButtonClick(true)}
            >
              Admin tools
            </TextButton>
          )}
        </Box>
      </Box>

      {displayAddPropertyModal && (
        <AddPropertyModal
          isOpen={true}
          onClose={() => setDisplayAddPropertyModal(false)}
          behavior={AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT}
        />
      )}

      {displayRentalProtectionSelectionsModal && (
        <RentalProtectionSelectionsModal
          onClose={() => setDisplayRentalProtectionSelectionsModal(false)}
          onDropRentalProtectionClick={handleDropRentalProtectionClick}
          premium={rentalProtectionPremium}
          monthlyRent={price}
          isSecurityDepositWaived={isSecurityDepositWaived}
          securityDepositAmount={securityDeposit || 0}
          showDropRentalProtectionRequestLink={false}
        />
      )}

      {showPolicyDropModal && (
        <RentGuaranteePolicyDropModal
          onClose={() => setShowPolicyDropModal(false)}
          coverageAmount={numberUtils.asCurrency(coverage)}
          handleRentGuaranteePolicyDrop={handleRentGuaranteePolicyDrop}
          legalDisclaimer={legalDisclaimer}
          premiumAmount={numberUtils.asCurrency(rentalProtectionPremium)}
        />
      )}

      {showSecurityDepositUpdateModal && (
        <SecurityDepositUpdateOnRentGuaranteeDropModal
          close={() => setShowSecurityDepositUpdateModal(false)}
          handleDropRentalProtectionSecurityDepositCancel={() => setShowSecurityDepositUpdateModal(false)}
          handleDropRentalProtectionSecurityDepositExit={() => setShowSecurityDepositUpdateModal(false)}
          handleSecurityDepositUpdate={handleSecurityDepositUpdate}
          securityDeposit={numberUtils.asCurrency(securityDeposit)}
          listingAlias={listingAlias}
        />
      )}

      {showSecurityDepositUpdateFailureModal && (
        <SecurityDepositUpdateFailureModal close={() => setShowSecurityDepositUpdateFailureModal(false)} />
      )}
    </Box>
  );
};

export default PropertyCard;
